/**
 * PP Gate — the pre-production sign-off, as its own step on the Style File.
 *
 * This decision used to be invisible: approving the sample silently advanced the
 * style to `pp_approved`. But the buyer approving a sample ("this garment is
 * right") and the factory clearing bulk ("we're ready to cut") are two different
 * calls made by two different people, and the second is, per the product spec,
 * the single most important event in an order's life — half of all delays trace
 * back to it. So it gets a card, a button, and an audit entry of its own.
 *
 * Rendered as a checklist rather than a bare button because a real PP meeting is
 * a checklist. Showing the conditions tells the approver what they're attesting
 * to, separates what the system verified from what needs a human call, and makes
 * the resulting audit entry mean something.
 *
 * Sits between Sampling and Sourcing: it consumes the sample, and it unlocks
 * issuing materials to cutting.
 *
 * @see packages/domain/src/garment/samples/approve-pp-gate.ts
 */
import { type ChangeEvent, useState } from "react";
import { Check, Lock, ShieldCheck, X } from "lucide-react";
import {
  Button,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  StatusPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { SectionCard } from "./style-cockpit-chrome.js";

export function PpGateCard({ styleId }: { styleId: string }) {
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState("");

  const gate = trpc.samples.ppGate.useQuery({ styleId });

  const approve = trpc.samples.approvePpGate.useMutation({
    onSuccess: async () => {
      setConfirmOpen(false);
      setNote("");
      await Promise.all([
        utils.samples.ppGate.invalidate({ styleId }),
        utils.styles.get.invalidate({ id: styleId }),
        utils.styles.list.invalidate(),
        utils.materials.getForStyle.invalidate({ styleId }),
        utils.auditLog.list.invalidate(),
      ]);
      toast.success("PP approved — bulk production unlocked");
    },
    onError: (e: { message: string }) => toast.error("Couldn't approve PP", { description: e.message }),
  });

  const data = gate.data;
  const approved = data?.status === "approved";

  // No pill in the approved state: the body says it in 16px with a green check,
  // and a second "Approved" in the header is the same word twice on one card.
  const action = approved ? null : data?.status === "ready" ? (
    <StatusPill tone="warn" size="sm" dot>Waiting for sign-off</StatusPill>
  ) : (
    <StatusPill tone="muted" size="sm" dot>Not ready</StatusPill>
  );

  return (
    <SectionCard
      title="PP Gate"
      meta="Pre-production sign-off · unlocks cutting"
      id="sec-pp-gate"
      action={action}
    >
      {gate.isLoading ? (
        <p className="text-sm text-muted">Loading PP gate…</p>
      ) : !data ? (
        <p className="text-sm text-muted">Couldn't load the PP gate.</p>
      ) : approved ? (
        /* The done state has to read at a glance — this is the answer to "can we
           cut?", and a grey box reading "Signed off · 19 Jul 2026, 19:02" made
           the single most consequential fact on the page look like a log line.
           Centred and quiet: the verdict in green, the date under it, the
           meeting note in muted prose. Nothing here is actionable, so nothing
           competes with the cards above and below that are. */
        <div className="rounded-md border border-line bg-bg px-4 py-6 text-center">
          <p className="flex items-center justify-center gap-2 text-lg font-semibold leading-tight text-ok">
            <ShieldCheck size={20} />
            Pre-production approved
          </p>
          <p className="mt-1 text-sm text-muted">
            {data.approvedAt ? formatDate(data.approvedAt) : "Signed off"}
            {data.overridden ? " · forced without an approved sample" : ""}
          </p>
          {data.note ? (
            <p className="mx-auto mt-3.5 max-w-lg text-sm leading-relaxed text-muted">
              “{data.note}”
            </p>
          ) : null}
        </div>
      ) : (
        <>
          {/* The meeting's checklist — what's satisfied, and what still needs a call. */}
          <ul className="flex flex-col divide-y divide-line rounded-sm border border-line">
            {data.checks.map((c) => (
              <li key={c.key} className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3.5 py-2.5">
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                    c.met ? "bg-ok/15 text-ok" : c.blocking ? "bg-bad/10 text-bad" : "bg-warn/15 text-warn"
                  }`}
                >
                  {c.met ? <Check size={11} /> : c.blocking ? <X size={11} /> : <span className="text-xs font-bold">!</span>}
                </span>
                <span className="text-sm text-ink">{c.label}</span>
                <span className="ml-auto text-xs text-muted">{c.detail}</span>
              </li>
            ))}
          </ul>

          <div className="mt-3 flex flex-col items-stretch gap-2">
            <Button size="lg" onClick={() => setConfirmOpen(true)} disabled={!data.canApprove}>
              <ShieldCheck size={15} />
              Approve PP — unlock bulk production
            </Button>
            <p className="text-center text-xs text-muted">
              {data.canApprove
                ? "Approving lets the floor draw materials and cut."
                : "Blocked until the sample is approved and the costing locked."}
            </p>
          </div>
        </>
      )}

      <DialogRoot open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Approve the PP gate?</DialogTitle>
            <DialogDescription>
              This clears bulk production — materials can be issued to cutting from here on. It's
              recorded against your name and can't be edited later.
            </DialogDescription>
          </DialogHeader>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink">PP meeting note (optional)</span>
            <textarea
              rows={3}
              value={note}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value.slice(0, 2000))}
              placeholder="e.g. Trims confirmed, collar rib matched to Pantone."
              disabled={approve.isPending}
              className="w-full resize-y rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-sky-500/30"
            />
          </label>

          <DialogFooter>
            <Button intent="ghost" onClick={() => setConfirmOpen(false)} disabled={approve.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => approve.mutate({ styleId, note: note.trim() === "" ? null : note.trim() })}
              disabled={approve.isPending}
            >
              {approve.isPending ? "Approving…" : "Approve PP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </SectionCard>
  );
}

/** The Sourcing card's locked note when the gate hasn't been signed off. */
export function PpLockNote() {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted">
      <Lock size={11} />
      Approve PP to issue
    </span>
  );
}
